import React, { useMemo } from 'react'
import styles from './AdjudicadosItemCard.module.scss'
import { CalendarDays, MapPin, Pickaxe, DollarSign, Building2, ExternalLink } from 'lucide-react'
import { iconJPG } from '../../UI/ProvinceIcons/IconsData'
import { useActionGuard } from '../../../hooks/useActionGuard.ts'
import ArgentinaIcono from '../../../assets/images/argentinaIcon.jpg'

type ProvinceIcon = {
  name: string
  icon: string
}

type Jurisdiction = {
  name?: string | null
} | null

type Organism = {
  name?: string | null
} | null

type WorkType = {
  name?: string | null
} | null

type AwardedProject = {
  id: number | string
  name?: string | null
  monto?: number | string | null
  organismo?: Organism
  jurisdiccion?: Jurisdiction
  obra_type?: WorkType
  adjudicatedDate?: string | null
  province?: string | null
}

interface AdjudicadosItemCardProps {
  item: AwardedProject
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const day = date.getUTCDate().toString().padStart(2, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

const formatCurrency = (value?: number | string | null) => {
  if (value === null || value === undefined) {
    return ''
  }
  const numericValue = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(numericValue)) {
    return ''
  }
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(numericValue)
}

const AdjudicadosItemCard: React.FC<AdjudicadosItemCardProps> = ({ item }) => {
  const { guard } = useActionGuard()

  const provinceIcon = useMemo(() => {
    const match = (iconJPG as ProvinceIcon[]).find(icon => icon.name === item?.jurisdiccion?.name)
    return match?.icon || ArgentinaIcono
  }, [item?.jurisdiccion?.name])

  const formattedBudget = formatCurrency(item?.monto)
  const adjudicationDate = formatDate(item?.adjudicatedDate)

  const handleOpen = () => {
    window.open(`/obraspublicas/proyectos-adjudicados/${item?.id}`, '_blank')
  }

  const infoItems = useMemo(
    () =>
      [
        item?.province
          ? {
              key: 'province',
              icon: <MapPin size={16} />,
              label: 'Ubicación',
              value: item.province,
            }
          : null,
        item?.obra_type?.name
          ? {
              key: 'type',
              icon: <Pickaxe size={16} />,
              label: 'Tipo de obra',
              value: item.obra_type.name,
            }
          : null,
        item?.organismo?.name
          ? {
              key: 'organism',
              icon: <Building2 size={16} />,
              label: 'Organismo',
              value: item.organismo.name,
            }
          : null,
        formattedBudget
          ? {
              key: 'budget',
              icon: <DollarSign size={18} />,
              label: 'Presupuesto adjudicado',
              value: `$${formattedBudget}`,
            }
          : null,
        {
          key: 'awardDate',
          icon: <CalendarDays size={18} />,
          label: 'Fecha de adjudicación',
          value: adjudicationDate || 'Sin fecha definida',
        },
      ].filter((detail): detail is { key: string; icon: React.ReactNode; label: string; value: string } => Boolean(detail)),
    [adjudicationDate, formattedBudget, item?.obra_type?.name, item?.organismo?.name, item?.province]
  )

  return (
    <article className={styles.card}>
      <div className={styles.header}>
        <div className={styles.graphic}>
          <img src={provinceIcon || '/placeholder.svg'} alt={`Mapa de ${item?.jurisdiccion?.name || 'Argentina'}`} />
        </div>
        <div className={styles.headerContent}>
          <h3 className={styles.title}>{item?.name}</h3>
          <p className={styles.subline}>
            {[item?.jurisdiccion?.name, item?.province].filter(Boolean).join(' • ') || 'Información no disponible'}
          </p>
        </div>
      </div>
      <div className={styles.detailsGrid}>
        {infoItems.map(detail => (
          <div key={detail.key} className={styles.detailItem}>
            <div className={styles.detailIcon}>{detail.icon}</div>
            <div className={styles.detailContent}>
              <span className={styles.detailLabel}>{detail.label}</span>
              <span className={styles.detailValue}>{detail.value}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.footer}>
        <button className={styles.cta} onClick={guard("quote_project", handleOpen)} aria-label={`Ver más detalles de ${item?.name || 'proyecto adjudicado'}`}>
          <span>Ver más detalles</span>
          <ExternalLink size={16} />
        </button>
      </div>
    </article>
  )
}

export default AdjudicadosItemCard

